import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import {
  PrismButton,
  PrismIllustration,
  PrismModal,
} from '@mylighthouse/prism-ember';
import type { IconName } from '@mylighthouse/prism-ember/types/components/prism/icon';
import type { IllustrationName } from '@mylighthouse/prism-ember/types/components/prism/illustration';
import { t } from 'ember-intl';
import GeneralNavigationIntercomHelpLink from 'lumen/components/general/navigation/intercom-help-link';
import { CENTRAL_MAPPING_HELP_CENTER_LINKS } from 'lumen/utils/central-mapping-constants';

import styles from './modal-mapping-onboarding.module.css';

interface OnboardingStep {
  description: string;
  header: string;
  helpLink: string;
  illustration: IllustrationName;
  linkIcon?: IconName;
  linkText: string;
}

interface SettingsCentralMappingModalsModalMappingOnboardingSignature {
  Args: {
    onClose: () => void;
  };
}

export default class SettingsCentralMappingModalsModalMappingOnboarding extends Component<SettingsCentralMappingModalsModalMappingOnboardingSignature> {
  @service declare intl: Services['intl'];
  @service declare router: Services['router'];

  @tracked currentStep: number = 0;

  get currentStepData(): OnboardingStep | undefined {
    return this.steps[this.currentStep];
  }

  get isFirstStep(): boolean {
    return this.currentStep === 0;
  }

  get isLastStep(): boolean {
    return this.currentStep === this.steps.length - 1;
  }

  get steps(): OnboardingStep[] {
    const stepTwoDescription = this.router.currentRouteName?.startsWith(
      'hotel.',
    )
      ? this.intl.t(
          'settings.central_mapping.onboarding.mapping.step_two.description.hotel',
        )
      : this.intl.t(
          'settings.central_mapping.onboarding.mapping.step_two.description.market',
        );

    return [
      {
        illustration: '200x200_manual-mapping',
        header: this.intl.t(
          'settings.central_mapping.onboarding.mapping.step_one.header',
        ),
        description: this.intl.t(
          'settings.central_mapping.onboarding.mapping.step_one.description',
        ),
        linkText: this.intl.t(
          'settings.central_mapping.onboarding.mapping.step_one.link_text',
        ),
        helpLink: CENTRAL_MAPPING_HELP_CENTER_LINKS.MAPPING_PAGE,
        linkIcon: 'book',
      },
      {
        illustration: '200x200_auto-mapping',
        header: this.intl.t(
          'settings.central_mapping.onboarding.mapping.step_two.header',
        ),
        description: stepTwoDescription,
        linkText: this.intl.t(
          'settings.central_mapping.onboarding.mapping.step_two.link_text',
        ),
        helpLink: CENTRAL_MAPPING_HELP_CENTER_LINKS.MAPPING_PAGE,
        linkIcon: 'book',
      },
    ];
  }

  @action onClickBack(): void {
    if (this.currentStep > 0) {
      this.currentStep -= 1;
    }
  }

  @action onClickNext(): void {
    if (this.currentStep < this.steps.length - 1) {
      this.currentStep += 1;
    }
  }

  <template>
    <PrismModal
      @onClose={{@onClose}}
      class={{styles.onboarding-modal}}
      data-test-id="central-mapping-mapping-onboarding-modal"
    >
      <:headerContent>
        <h3
          class="flex w-full items-center justify-center pl-1000"
          data-test-id="central-mapping-mapping-onboarding-modal-header"
        >
          {{this.currentStepData.header}}
        </h3>
      </:headerContent>

      <:default>
        <section
          class={{styles.body}}
          data-test-id="central-mapping-mapping-onboarding-modal-content"
        >
          {{#if this.currentStepData}}
            <PrismIllustration
              @name={{this.currentStepData.illustration}}
              @size="unsafe_unconstrained"
            />

            <p
              class="paragraph-300--regular"
            >{{this.currentStepData.description}}</p>

            <GeneralNavigationIntercomHelpLink
              @articleLink={{this.currentStepData.helpLink}}
              @iconName="book"
              @iconPlacement="before"
              @isStandaloneLink={{true}}
              @linkText={{this.currentStepData.linkText}}
              @textSize="small"
              @type="text-and-icon"
              data-test-id="help-center-button"
            />
          {{/if}}
        </section>
      </:default>

      <:footer>
        <div
          class="flex flex-row
            {{if this.isFirstStep 'justify-end' 'justify-between'}}
            'items-center' px-500 py-300"
        >
          {{#unless this.isFirstStep}}
            <PrismButton
              @buttonType="secondary"
              @iconBefore="arrow-left"
              @label={{t "general.back"}}
              @onClick={{this.onClickBack}}
              data-test-id="central-mapping-mapping-onboarding-modal-back-button"
            />
          {{/unless}}

          {{#if this.isLastStep}}
            <PrismButton
              @label={{t
                "settings.central_mapping.onboarding.mapping.step_two.action"
              }}
              @onClick={{@onClose}}
              data-test-id="central-mapping-mapping-onboarding-modal-close-button"
            />
          {{else}}
            <PrismButton
              @iconAfter="arrow-right"
              @label={{t "general.next"}}
              @onClick={{this.onClickNext}}
              data-test-id="central-mapping-mapping-onboarding-modal-next-button"
            />
          {{/if}}
        </div>
      </:footer>
    </PrismModal>
  </template>
}
